import { useState, useEffect, useCallback, useRef } from 'react'
import { merchantApi } from '../api'
import { errorMessage } from './ErrorState'
import { useEscapeKey } from '../useEscapeKey'
import { enumLabel, useI18n } from '../i18n'
import type { MerchantRow, MerchantCategory, MerchantStatus, CountryCode } from '../types'
import { COUNTRY_FLAGS, MERCHANT_CATEGORIES, MERCHANT_STATUSES } from '../types'

// ── Constantes ────────────────────────────────────────────────────────────────

const COUNTRIES: CountryCode[] = ['SN', 'CI', 'ML', 'BF', 'GN', 'TG', 'BJ', 'NE', 'CM', 'GH', 'FR', 'BE', 'CA']
const STATUS_COLORS: Record<MerchantStatus, string> = {
  ACTIVE: 'var(--ok-fg)',
  SUSPENDED: 'var(--warn)',
  UNDER_REVIEW: 'var(--info)',
  TERMINATED: 'var(--crit)',
}

interface FormData {
  name: string
  url: string
  country: CountryCode
  category: MerchantCategory
  crawlFrequencyHours: number
}

const EMPTY_FORM: FormData = {
  name: '',
  url: '',
  country: 'SN',
  category: 'retail',
  crawlFrequencyHours: 72,
}

// ── Composant principal ───────────────────────────────────────────────────────

export function MerchantsManager() {
  const { t, fmt } = useI18n()
  const [merchants, setMerchants] = useState<MerchantRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filtres
  const [search, setSearch] = useState('')
  const [filterCountry, setFilterCountry] = useState<string>('')
  const [filterStatus, setFilterStatus] = useState<string>('')
  const [filterCategory, setFilterCategory] = useState<string>('')

  // Formulaire ajout/edition
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<FormData>({ ...EMPTY_FORM })
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Import CSV
  const [showImport, setShowImport] = useState(false)
  const [csvRows, setCsvRows] = useState<Array<{ name: string; url: string; country: string; category: string; crawlFrequencyHours?: number }>>([])
  const [csvFileName, setCsvFileName] = useState('')
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<{ imported: number; errors: Array<{ row: number; name: string; error: string }> } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEscapeKey(showForm, () => setShowForm(false))
  useEscapeKey(showImport, () => setShowImport(false))

  // Scan N1
  const [scanning, setScanning] = useState(false)
  const [scanProgress, setScanProgress] = useState<{ total: number; done: number; failed: number; active: number } | null>(null)
  const scanPollRef = useRef<ReturnType<typeof setInterval>>()

  // Scan individuel par marchand
  const [merchantScans, setMerchantScans] = useState<Record<string, { jobId: string; state: string; progress: number }>>({})

  // Tri
  const [sortCol, setSortCol] = useState<'name' | 'country' | 'category' | 'status' | 'crawlFrequencyHours' | 'lastCrawledAt'>('name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  // Message flash
  const [flash, setFlash] = useState<{ type: 'ok' | 'error'; text: string } | null>(null)
  const flashTimer = useRef<ReturnType<typeof setTimeout>>()

  const showFlash = useCallback((type: 'ok' | 'error', text: string) => {
    setFlash({ type, text })
    if (flashTimer.current) clearTimeout(flashTimer.current)
    flashTimer.current = setTimeout(() => setFlash(null), 6000)
  }, [])

  // ── Chargement ──────────────────────────────────────────────────────────────

  const loadMerchants = useCallback(async () => {
    try {
      setLoading(true)
      const data = await merchantApi.findAll()
      setMerchants(data)
      setError(null)
    } catch (err: unknown) {
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadMerchants()
  }, [loadMerchants])

  // ── Filtrage ────────────────────────────────────────────────────────────────

  const filtered = merchants.filter((m) => {
    if (search) {
      const q = search.toLowerCase()
      if (!m.name.toLowerCase().includes(q) && !m.url.toLowerCase().includes(q)) return false
    }
    if (filterCountry && m.country !== filterCountry) return false
    if (filterStatus && m.status !== filterStatus) return false
    if (filterCategory && m.category !== filterCategory) return false
    return true
  }).sort((a, b) => {
    const col = sortCol
    let va: string | number = a[col] ?? ''
    let vb: string | number = b[col] ?? ''
    if (col === 'lastCrawledAt') {
      va = va ? new Date(va as string).getTime() : 0
      vb = vb ? new Date(vb as string).getTime() : 0
    }
    if (col === 'crawlFrequencyHours') {
      va = Number(va)
      vb = Number(vb)
    }
    if (typeof va === 'number' && typeof vb === 'number') {
      return sortDir === 'asc' ? va - vb : vb - va
    }
    const sa = String(va).toLowerCase()
    const sb = String(vb).toLowerCase()
    return sortDir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa)
  })

  const toggleSort = (col: typeof sortCol) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortCol(col)
      setSortDir('asc')
    }
  }

  const sortIcon = (col: typeof sortCol) =>
    sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''

  // ── Formulaire ──────────────────────────────────────────────────────────────

  const openAdd = () => {
    setEditingId(null)
    setForm({ ...EMPTY_FORM })
    setFormError(null)
    setShowForm(true)
    setShowImport(false)
  }

  const openEdit = (m: MerchantRow) => {
    setEditingId(m.id)
    setForm({
      name: m.name,
      url: m.url,
      country: m.country,
      category: m.category,
      crawlFrequencyHours: m.crawlFrequencyHours,
    })
    setFormError(null)
    setShowForm(true)
    setShowImport(false)
  }

  const handleSubmit = async () => {
    if (!form.name.trim()) { setFormError(t('merchants.nameRequired')); return }
    if (!form.url.trim()) { setFormError(t('merchants.urlRequired')); return }

    let url = form.url.trim()
    if (!url.startsWith('http')) url = 'https://' + url

    setSaving(true)
    setFormError(null)

    try {
      if (editingId) {
        await merchantApi.update(editingId, { ...form, url })
        showFlash('ok', t('merchants.flashUpdated', { name: form.name }))
      } else {
        await merchantApi.create({ ...form, url })
        showFlash('ok', t('merchants.flashAdded', { name: form.name }))
      }
      setShowForm(false)
      await loadMerchants()
    } catch (err: unknown) {
      setFormError(errorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  // ── Actions status ──────────────────────────────────────────────────────────

  const handleSuspend = async (m: MerchantRow) => {
    try {
      await merchantApi.suspend(m.id)
      showFlash('ok', t('merchants.flashSuspended', { name: m.name }))
      await loadMerchants()
    } catch (err: unknown) {
      showFlash('error', errorMessage(err))
    }
  }

  const handleActivate = async (m: MerchantRow) => {
    try {
      await merchantApi.activate(m.id)
      showFlash('ok', t('merchants.flashReactivated', { name: m.name }))
      await loadMerchants()
    } catch (err: unknown) {
      showFlash('error', errorMessage(err))
    }
  }

  const handleDelete = async (m: MerchantRow) => {
    if (!confirm(t('common.deleteConfirm', { name: m.name }))) return
    try {
      await merchantApi.remove(m.id)
      showFlash('ok', t('merchants.flashDeleted', { name: m.name }))
      await loadMerchants()
    } catch (err: unknown) {
      showFlash('error', errorMessage(err))
    }
  }

  // ── Import CSV ──────────────────────────────────────────────────────────────

  const openImport = () => {
    setShowImport(true)
    setShowForm(false)
    setCsvRows([])
    setCsvFileName('')
    setImportResult(null)
  }

  const parseCsvFile = (file: File) => {
    setCsvFileName(file.name)
    setImportResult(null)

    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result as string
      if (!text) return

      const lines = text.split(/\r?\n/).filter((l) => l.trim())
      if (lines.length < 2) {
        showFlash('error', t('merchants.importTooShort'))
        return
      }

      // Parse header
      const sep = lines[0].includes(';') ? ';' : ','
      const headers = lines[0].split(sep).map((h) => h.trim().toLowerCase().replace(/['"]/g, ''))

      const nameIdx = headers.findIndex((h) => h === 'name' || h === 'nom')
      const urlIdx = headers.findIndex((h) => h === 'url' || h === 'site')
      const countryIdx = headers.findIndex((h) => h === 'country' || h === 'pays')
      const categoryIdx = headers.findIndex((h) => h === 'category' || h === 'categorie' || h === 'categorie')
      const freqIdx = headers.findIndex((h) => h.includes('freq') || h.includes('crawl'))

      if (nameIdx === -1 || urlIdx === -1) {
        showFlash('error', t('merchants.importMissingCols'))
        return
      }

      const rows = []
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(sep).map((c) => c.trim().replace(/^["']|["']$/g, ''))
        if (cols.length < 2) continue

        rows.push({
          name: cols[nameIdx] || '',
          url: cols[urlIdx] || '',
          country: countryIdx !== -1 ? cols[countryIdx] || 'SN' : 'SN',
          category: categoryIdx !== -1 ? cols[categoryIdx] || 'retail' : 'retail',
          crawlFrequencyHours: freqIdx !== -1 ? parseInt(cols[freqIdx], 10) || undefined : undefined,
        })
      }

      setCsvRows(rows)
    }
    reader.readAsText(file)
  }

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) parseCsvFile(file)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) parseCsvFile(file)
  }

  const handleImportSubmit = async () => {
    if (csvRows.length === 0) return
    setImporting(true)
    setImportResult(null)

    try {
      const result = await merchantApi.importCsv(csvRows)
      setImportResult(result)
      if (result.imported > 0) {
        showFlash('ok', t('merchants.importSuccess', { n: result.imported }))
        await loadMerchants()
      }
    } catch (err: unknown) {
      showFlash('error', errorMessage(err))
    } finally {
      setImporting(false)
    }
  }

  // ── Nettoyage du polling au demontage ───────────────────────────────────────

  useEffect(() => {
    return () => {
      if (scanPollRef.current) clearInterval(scanPollRef.current)
    }
  }, [])

  const stopPolling = useCallback(() => {
    if (scanPollRef.current) {
      clearInterval(scanPollRef.current)
      scanPollRef.current = undefined
    }
  }, [])

  const startPolling = useCallback((totalEnqueued: number) => {
    stopPolling()
    setScanProgress({ total: totalEnqueued, done: 1, failed: 0, active: totalEnqueued })

    const initialCompleted = { value: -1 }
    const startedAt = Date.now()

    scanPollRef.current = setInterval(async () => {
      try {
        const stats = await merchantApi.getCrawlProgress()

        if (initialCompleted.value === -1) {
          initialCompleted.value = stats.completed + stats.failed
        }

        const batchCompleted = (stats.completed + stats.failed) - initialCompleted.value
        const done = Math.min(Math.max(1, batchCompleted), totalEnqueued)
        const remaining = stats.waiting + stats.active
        const elapsed = Date.now() - startedAt

        setScanProgress({
          total: totalEnqueued,
          done,
          failed: Math.max(0, batchCompleted > 0 ? Math.min(stats.failed, batchCompleted) : 0),
          active: stats.active,
        })

        const isFinished = remaining === 0 || done >= totalEnqueued
        if (isFinished && elapsed > 3000) {
          stopPolling()
          setScanning(false)
          setScanProgress((prev) => prev ? { ...prev, done: prev.total, active: 0 } : null)
          showFlash('ok', t('merchants.scanDone', { n: totalEnqueued }))
          loadMerchants()
          setTimeout(() => setScanProgress(null), 8000)
        }
      } catch {
        // Ignore polling errors silently
      }
    }, 2000)
  }, [stopPolling, showFlash, loadMerchants, t])

  // ── Scan N1 global ─────────────────────────────────────────────────────────

  const handleCrawlAll = async () => {
    setScanning(true)
    setScanProgress(null)
    try {
      const result = await merchantApi.crawlAll()
      if (result.enqueued > 0) {
        startPolling(result.enqueued)
      } else {
        // Message du serveur : il explique pourquoi rien n'a été mis en file.
        showFlash('ok', result.message)
        setScanning(false)
      }
    } catch (err: unknown) {
      showFlash('error', errorMessage(err) || t('merchants.scanAllError'))
      setScanning(false)
    }
  }

  // ── Scan individuel avec suivi ──────────────────────────────────────────

  const handleScanOne = async (m: MerchantRow) => {
    try {
      const result = await merchantApi.triggerCrawl(m.id)
      setMerchantScans((prev) => ({ ...prev, [m.id]: { jobId: result.jobId, state: 'waiting', progress: 0 } }))

      const pollInterval = setInterval(async () => {
        try {
          const status = await merchantApi.getJobStatus(result.jobId)
          setMerchantScans((prev) => {
            if (!prev[m.id]) return prev
            return { ...prev, [m.id]: { ...prev[m.id], state: status.state, progress: status.progress } }
          })

          if (status.state === 'completed' || status.state === 'failed') {
            clearInterval(pollInterval)
            if (status.state === 'completed') {
              showFlash('ok', t('merchants.scanOneDone', { name: m.name }))
            } else {
              showFlash('error', t('merchants.scanOneFailed', { name: m.name }))
            }
            loadMerchants()
            setTimeout(() => {
              setMerchantScans((prev) => {
                const next = { ...prev }
                delete next[m.id]
                return next
              })
            }, 3000)
          }
        } catch {
          clearInterval(pollInterval)
          setMerchantScans((prev) => {
            const next = { ...prev }
            delete next[m.id]
            return next
          })
        }
      }, 1500)
    } catch (err: unknown) {
      showFlash('error', errorMessage(err) || t('merchants.scanOneError'))
    }
  }

  // ── Rendu ───────────────────────────────────────────────────────────────────

  return (
    <div className="mm">
      {/* Flash message */}
      {flash && (
        <div className={`mm-flash mm-flash-${flash.type}`}>
          {flash.type === 'ok' ? '✓' : '✗'} {flash.text}
        </div>
      )}

      {/* Header */}
      <div className="mm-header">
        <div className="mm-header-left">
          <h2>{t('merchants.title')}</h2>
          <span className="mm-count">{t('merchants.count', { n: merchants.length })}</span>
        </div>
        <div className="mm-header-actions">
          <button
            className="mm-btn mm-btn-scan"
            onClick={handleCrawlAll}
            disabled={scanning || merchants.length === 0}
          >
            {scanning ? t('merchants.scanning') : t('merchants.scanAll')}
          </button>
          <button className="mm-btn mm-btn-primary" onClick={openAdd}>{t('merchants.addBtn')}</button>
          <button className="mm-btn mm-btn-secondary" onClick={openImport}>{t('merchants.importCsv')}</button>
        </div>
      </div>

      {/* Barre de progression scan N1 */}
      {scanProgress && (
        <div className="mm-progress-card">
          <div className="mm-progress-header">
            <span className="mm-progress-label">
              {scanProgress.done >= scanProgress.total
                ? t('merchants.progressDone')
                : t('merchants.progressRunning', { done: scanProgress.done, total: scanProgress.total })}
            </span>
            <span className="mm-progress-pct">
              {Math.round((scanProgress.done / scanProgress.total) * 100)}%
            </span>
          </div>
          <div className="mm-progress-track">
            <div
              className={`mm-progress-fill${scanProgress.done >= scanProgress.total ? ' mm-progress-complete' : ''}`}
              style={{ transform: `scaleX(${scanProgress.done / scanProgress.total})` }}
            />
          </div>
          <div className="mm-progress-details">
            <span>{t('merchants.progressCompleted', { n: scanProgress.done })}</span>
            {scanProgress.active > 0 && <span>{t('merchants.progressActive', { n: scanProgress.active })}</span>}
            {scanProgress.failed > 0 && (
              <span className="mm-progress-failed">{t('merchants.progressFailed', { n: scanProgress.failed })}</span>
            )}
          </div>
        </div>
      )}

      {/* Modale ajout/edition */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingId ? t('merchants.formEdit') : t('merchants.formNew')}</h3>
              <button className="modal-close" aria-label={t('common.close')} onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {formError && <div className="mm-form-error">{formError}</div>}

              <div className="mm-form-grid">
                <label className="mm-field">
                  <span>{t('merchants.fieldName')}</span>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder={t('merchants.namePlaceholder')}
                    autoFocus
                  />
                </label>

                <label className="mm-field">
                  <span>{t('merchants.fieldUrl')}</span>
                  <input
                    type="text"
                    value={form.url}
                    onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                    placeholder={t('merchants.urlPlaceholder')}
                  />
                </label>

                <label className="mm-field">
                  <span>{t('common.country')}</span>
                  <select
                    value={form.country}
                    onChange={(e) => setForm((f) => ({ ...f, country: e.target.value as CountryCode }))}
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>{COUNTRY_FLAGS[c]} {enumLabel(t, 'country', c, c)}</option>
                    ))}
                  </select>
                </label>

                <label className="mm-field">
                  <span>{t('common.category')}</span>
                  <select
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as MerchantCategory }))}
                  >
                    {MERCHANT_CATEGORIES.map((c) => (
                      <option key={c} value={c}>{enumLabel(t, 'category', c, c)}</option>
                    ))}
                  </select>
                </label>

                <label className="mm-field">
                  <span>{t('merchants.fieldFrequency')}</span>
                  <input
                    type="number"
                    min={1}
                    value={form.crawlFrequencyHours}
                    onChange={(e) => setForm((f) => ({ ...f, crawlFrequencyHours: parseInt(e.target.value, 10) || 72 }))}
                  />
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="mm-btn mm-btn-ghost" onClick={() => setShowForm(false)}>{t('common.cancel')}</button>
              <button className="mm-btn mm-btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? t('common.saving') : editingId ? t('merchants.update') : t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Import CSV */}
      {showImport && (
        <div className="modal-overlay" onClick={() => setShowImport(false)}>
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('merchants.importCsv')}</h3>
              <button className="modal-close" aria-label={t('common.close')} onClick={() => setShowImport(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="mm-import-help">{t('merchants.importHelp')}</p>

              <div
                className="mm-dropzone"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                {csvFileName ? (
                  <span>{t('merchants.importFile', { file: csvFileName, n: csvRows.length })}</span>
                ) : (
                  <span>{t('merchants.importDrop')}</span>
                )}
              </div>

              {csvRows.length > 0 && (
                <div className="mm-csv-preview">
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">#</th>
                        <th scope="col">{t('common.name')}</th>
                        <th scope="col">{t('common.url')}</th>
                        <th scope="col">{t('common.country')}</th>
                        <th scope="col">{t('common.category')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvRows.slice(0, 10).map((r, i) => (
                        <tr key={i}>
                          <td className="td-muted">{i + 1}</td>
                          <td>{r.name}</td>
                          <td className="td-muted">{r.url}</td>
                          <td>{r.country}</td>
                          <td>{r.category}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {csvRows.length > 10 && (
                    <p className="mm-csv-more">{t('merchants.importMore', { n: csvRows.length - 10 })}</p>
                  )}
                </div>
              )}

              {importResult && (
                <div className="mm-import-result">
                  <div className={`mm-flash mm-flash-${importResult.errors.length === 0 ? 'ok' : 'error'}`}>
                    {t('merchants.importedCount', { n: importResult.imported })}
                    {importResult.errors.length > 0 && t('merchants.importErrorCount', { n: importResult.errors.length })}
                  </div>
                  {importResult.errors.length > 0 && (
                    <ul className="mm-import-errors">
                      {importResult.errors.map((e, i) => (
                        <li key={i}>{t('merchants.importRowError', { row: e.row, name: e.name, error: e.error })}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="mm-csv-template">
                <strong>{t('merchants.csvExample')}</strong>
                <code>name;url;country;category{'\n'}Orange Money SN;https://orange-money.sn;SN;telecom{'\n'}Wave Senegal;https://wave.com;SN;finance</code>
              </div>
            </div>
            {csvRows.length > 0 && (
              <div className="modal-footer">
                <button className="mm-btn mm-btn-ghost" onClick={() => { setCsvRows([]); setCsvFileName('') }}>
                  {t('merchants.importClear')}
                </button>
                <button className="mm-btn mm-btn-primary" onClick={handleImportSubmit} disabled={importing}>
                  {importing ? t('common.importing') : t('merchants.importSubmit', { n: csvRows.length })}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="mm-filters">
        <input
          type="text"
          className="mm-search"
          placeholder={t('merchants.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)}>
          <option value="">{t('common.allCountries')}</option>
          {COUNTRIES.map((c) => (
            <option key={c} value={c}>{COUNTRY_FLAGS[c]} {enumLabel(t, 'country', c, c)}</option>
          ))}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">{t('merchants.allStatuses')}</option>
          {MERCHANT_STATUSES.map((s) => (
            <option key={s} value={s}>{enumLabel(t, 'merchantStatus', s, s)}</option>
          ))}
        </select>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
          <option value="">{t('merchants.allCategories')}</option>
          {MERCHANT_CATEGORIES.map((c) => (
            <option key={c} value={c}>{enumLabel(t, 'category', c, c)}</option>
          ))}
        </select>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="mm-loading">{t('common.loading')}</div>
      ) : error ? (
        <div className="mm-error">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="mm-empty">
          {merchants.length === 0 ? t('merchants.empty') : t('merchants.emptyFiltered')}
        </div>
      ) : (
        <div className="table-card">
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th className="th-sortable" onClick={() => toggleSort('name')}>{t('common.merchant')}{sortIcon('name')}</th>
                  <th className="th-sortable" onClick={() => toggleSort('country')}>{t('common.country')}{sortIcon('country')}</th>
                  <th className="th-sortable" onClick={() => toggleSort('category')}>{t('common.category')}{sortIcon('category')}</th>
                  <th className="th-sortable" onClick={() => toggleSort('status')}>{t('common.status')}{sortIcon('status')}</th>
                  <th className="th-sortable" onClick={() => toggleSort('crawlFrequencyHours')}>{t('merchants.col.frequency')}{sortIcon('crawlFrequencyHours')}</th>
                  <th className="th-sortable" onClick={() => toggleSort('lastCrawledAt')}>{t('merchants.col.lastCrawl')}{sortIcon('lastCrawledAt')}</th>
                  <th scope="col">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <div className="merchant-name">{m.name}</div>
                      <div className="merchant-meta">
                        <a href={m.url} target="_blank" rel="noopener noreferrer">{m.url}</a>
                      </div>
                    </td>
                    <td>
                      <span title={enumLabel(t, 'country', m.country, m.country)}>
                        {COUNTRY_FLAGS[m.country]} {m.country}
                      </span>
                    </td>
                    <td>{enumLabel(t, 'category', m.category, m.category)}</td>
                    <td>
                      <span
                        className="mm-status-badge"
                        style={{ background: STATUS_COLORS[m.status] + '18', color: STATUS_COLORS[m.status] }}
                      >
                        {enumLabel(t, 'merchantStatus', m.status, m.status)}
                      </span>
                    </td>
                    <td className="td-muted">{t('merchants.hours', { n: m.crawlFrequencyHours })}</td>
                    <td className="td-muted">
                      {m.lastCrawledAt ? fmt.dateTime(m.lastCrawledAt) : t('common.never')}
                    </td>
                    <td>
                      <div className="mm-actions">
                        {merchantScans[m.id] ? (
                          <div className="mm-scan-indicator">
                            <div className="mm-scan-indicator-track">
                              <div
                                className={`mm-scan-indicator-fill${merchantScans[m.id].state === 'completed' ? ' mm-scan-done' : merchantScans[m.id].state === 'failed' ? ' mm-scan-fail' : ''}`}
                                style={{ transform: `scaleX(${merchantScans[m.id].state === 'completed' ? 1 : merchantScans[m.id].state === 'failed' ? 1 : Math.max(0.05, merchantScans[m.id].progress / 100)})` }}
                              />
                            </div>
                            <span className={`mm-scan-indicator-label${merchantScans[m.id].state === 'completed' ? ' mm-label-ok' : merchantScans[m.id].state === 'failed' ? ' mm-label-fail' : ''}`}>
                              {merchantScans[m.id].state === 'completed'
                                ? 'OK'
                                : merchantScans[m.id].state === 'failed'
                                  ? t('merchants.scanFailedShort')
                                  : `${merchantScans[m.id].progress}%`}
                            </span>
                          </div>
                        ) : (
                          <button
                            className="mm-btn-sm mm-btn-scan-sm"
                            onClick={() => handleScanOne(m)}
                            disabled={scanning}
                            title={t('merchants.scanOneTitle')}
                          >
                            {t('merchants.scanOne')}
                          </button>
                        )}
                        <button className="mm-btn-sm mm-btn-edit" onClick={() => openEdit(m)} title={t('common.edit')}>{t('common.edit')}</button>
                        {m.status === 'ACTIVE' ? (
                          <button className="mm-btn-sm mm-btn-warn" onClick={() => handleSuspend(m)} title={t('merchants.suspend')}>{t('merchants.suspend')}</button>
                        ) : m.status === 'SUSPENDED' ? (
                          <button className="mm-btn-sm mm-btn-ok" onClick={() => handleActivate(m)} title={t('merchants.reactivate')}>{t('merchants.reactivate')}</button>
                        ) : null}
                        <button className="mm-btn-sm mm-btn-danger" onClick={() => handleDelete(m)} title={t('common.delete')}>{t('common.deleteShort')}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
